import { defineStore } from 'pinia'
import { ref } from 'vue'

/**
 * 优先级常量
 */
export enum ApiPriority {
  CRITICAL = 100, // 紧急任务（如用户发起的通话）
  HIGH = 10, // 用户主动聊天、即时操作
  NORMAL = 5, // 普通操作
  LOW = 1, // 后台活动、批量任务
}

/**
 * 队列任务接口
 */
interface QueueTask<T = any> {
  id: string
  action: () => Promise<T>
  priority: ApiPriority
  requester: string
  timestamp: number
  resolve: (value: T) => void
  reject: (error: any) => void
}

/**
 * API 优先级队列 Store
 *
 * 功能：
 * - 管理所有 AI API 调用，防止并发冲突
 * - 基于优先级调度任务
 * - 用户操作始终优先于后台活动
 * - 提供任务取消和队列管理功能
 */
export const useApiPriorityQueueStore = defineStore('apiPriorityQueue', () => {
  // 任务队列
  const queue = ref<QueueTask[]>([])

  // 是否正在处理任务
  const isLocked = ref(false)

  // 当前正在执行的任务
  const currentTask = ref<QueueTask | null>(null)

  // 统计信息
  const stats = ref({
    totalProcessed: 0,
    totalFailed: 0,
    averageWaitTime: 0,
    currentQueueSize: 0,
  })

  /**
   * 将任务加入队列
   * @param action 要执行的异步函数
   * @param priority 任务优先级
   * @param requester 任务名称（用于调试）
   * @returns Promise，在任务完成时 resolve
   */
  function enqueue<T = any>(
    action: () => Promise<T>,
    priority: ApiPriority = ApiPriority.NORMAL,
    requester: string = 'unknown',
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const task: QueueTask<T> = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        action,
        priority,
        requester,
        timestamp: Date.now(),
        resolve: resolve as any,
        reject,
      }

      queue.value.push(task)

      // 按优先级排序（高优先级在前）
      // 如果优先级相同，则按加入时间排序（FIFO）
      queue.value.sort((a, b) => {
        if (b.priority !== a.priority) {
          return b.priority - a.priority
        }
        return a.timestamp - b.timestamp
      })

      stats.value.currentQueueSize = queue.value.length

      console.debug(`📥 API Queue: Task "${requester}" enqueued (Priority: ${priority}, Queue size: ${queue.value.length})`)

      // 尝试处理队列
      processQueue()
    })
  }

  /**
   * 处理队列中的任务
   */
  async function processQueue() {
    // 如果已经在处理或队列为空，则返回
    if (isLocked.value || queue.value.length === 0) {
      return
    }

    // 锁定队列
    isLocked.value = true

    // 取出最高优先级的任务
    const task = queue.value.shift()

    if (!task) {
      isLocked.value = false
      return
    }

    currentTask.value = task
    stats.value.currentQueueSize = queue.value.length

    const waitTime = Date.now() - task.timestamp
    stats.value.averageWaitTime
      = (stats.value.averageWaitTime * stats.value.totalProcessed + waitTime)
        / (stats.value.totalProcessed + 1)

    console.debug(
      `🔓 API Queue: Lock acquired by "${task.requester}" (Priority: ${task.priority}, Wait time: ${waitTime}ms, Queue size: ${queue.value.length})`,
    )

    try {
      const result = await task.action()
      task.resolve(result)
      stats.value.totalProcessed++
      console.debug(`✅ API Queue: Task "${task.requester}" completed successfully`)
    }
    catch (error) {
      console.error(`❌ API Queue: Task "${task.requester}" failed:`, error)
      task.reject(error)
      stats.value.totalFailed++
    }
    finally {
      // 释放锁
      isLocked.value = false
      currentTask.value = null

      console.debug(`🔐 API Queue: Lock released by "${task.requester}"`)

      // 立即尝试处理下一个任务
      processQueue()
    }
  }

  /**
   * 检查队列中是否已有来自特定请求者的任务
   * @param requester 请求者名称
   * @returns 是否存在
   */
  function isQueued(requester: string): boolean {
    return queue.value.some(task => task.requester === requester)
  }

  /**
   * 取消队列中的特定任务
   * @param requester 请求者名称
   * @returns 被取消的任务数量
   */
  function cancel(requester: string): number {
    const beforeLength = queue.value.length
    queue.value = queue.value.filter((task) => {
      if (task.requester === requester) {
        task.reject(new Error('Task cancelled'))
        return false
      }
      return true
    })
    const cancelledCount = beforeLength - queue.value.length
    stats.value.currentQueueSize = queue.value.length

    if (cancelledCount > 0) {
      console.info(`🚫 API Queue: Cancelled ${cancelledCount} task(s) from "${requester}"`)
    }

    return cancelledCount
  }

  /**
   * 取消所有后台活动任务
   */
  function cancelBackgroundTasks(): number {
    return cancel('background_activity')
  }

  /**
   * 清空整个队列
   */
  function clear(): void {
    queue.value.forEach((task) => {
      task.reject(new Error('Queue cleared'))
    })
    queue.value = []
    stats.value.currentQueueSize = 0
    console.warn('🗑️ API Queue: Cleared all tasks')
  }

  /**
   * 获取队列状态信息
   */
  function getStatus() {
    return {
      queueSize: queue.value.length,
      isProcessing: isLocked.value,
      currentTask: currentTask.value
        ? {
            requester: currentTask.value.requester,
            priority: currentTask.value.priority,
            waitTime: Date.now() - currentTask.value.timestamp,
          }
        : null,
      nextTask: queue.value[0]
        ? {
            requester: queue.value[0].requester,
            priority: queue.value[0].priority,
            waitTime: Date.now() - queue.value[0].timestamp,
          }
        : null,
      stats: { ...stats.value },
    }
  }

  /**
   * 重置统计信息
   */
  function resetStats(): void {
    stats.value = {
      totalProcessed: 0,
      totalFailed: 0,
      averageWaitTime: 0,
      currentQueueSize: queue.value.length,
    }
    console.info('📊 API Queue: Stats reset')
  }

  return {
    // State
    queue,
    isLocked,
    currentTask,
    stats,

    // Actions
    enqueue,
    isQueued,
    cancel,
    cancelBackgroundTasks,
    clear,
    getStatus,
    resetStats,

    // Constants
    ApiPriority,
  }
})
